import type { SiteDetail, UnitRow } from "@plenum/shared/api";
import { useState } from "react";
import { fmtInt, fmtMw, fmtPct } from "../../lib/format";
import {
  COOLING_TYPE_LABEL,
  FRAME_LABEL,
  label,
  PRIME_MOVER_LABEL,
  UNIT_STATUS_LABEL,
} from "../../lib/tiers";
import { Pill } from "../Badge";

const EFF_SOURCE: Record<string, string> = {
  cems_measured_gross_hhv: "CEMS measured, gross HHV",
  eia923_allocated_hhv: "EIA-923 allocated, HHV",
  rated_estimate_lhv: "Rated estimate, ISO LHV",
};

export function UnitsTab({ detail, wide = false }: { detail: SiteDetail; wide?: boolean }) {
  const [showAll, setShowAll] = useState(true);
  const units = showAll ? detail.units : detail.units.filter((u) => u.in_band);
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-[12px]">
        <span style={{ color: "var(--muted)" }}>
          {detail.units.filter((u) => u.in_band).length} in band of {detail.units.length}. In-band
          rows are bright; CA steam parts stay for context.
        </span>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          show all units
        </label>
      </div>
      <div className="scroll-thin overflow-x-auto">
        <table className="tbl" data-testid="units-table">
          <thead>
            <tr>
              <th>Gen</th>
              <th title="Prime mover">PM</th>
              <th className="r">Nameplate</th>
              <th className="r">Summer</th>
              <th>Status</th>
              <th className="r">Year</th>
              <th className="r">Age</th>
              <th>OEM / model</th>
              <th>Frame</th>
              <th className="r" title="Latest capacity factor (EIA-923)">
                CF
              </th>
              <th className="r" title="Best available efficiency; hover for the basis">
                Eff.
              </th>
              <th className="r" title="June to September operating hours, 3-year CEMS average">
                Summer h
              </th>
              <th>Inlet cooling</th>
              <th className="r">Est. MWh/yr</th>
              <th className="r">Water m³/yr</th>
              <th className="r">Score</th>
              {wide ? <th>Technology</th> : null}
            </tr>
          </thead>
          <tbody>
            {units.map((u) => (
              <UnitTr key={u.id} u={u} wide={wide} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UnitTr({ u, wide }: { u: UnitRow; wide: boolean }) {
  const p = u.genset.performance ?? {};
  return (
    <tr
      className={u.in_band ? "in-band" : "out-band"}
      data-pm={u.prime_mover}
      data-in-band={u.in_band ? "1" : "0"}
    >
      <td className="num">{u.eia_generator_id ?? u.id}</td>
      <td title={PRIME_MOVER_LABEL[u.prime_mover]}>{u.prime_mover}</td>
      <td className="num r">{fmtMw(u.nameplate_mw)}</td>
      {wide ? <td className="num r">{fmtMw(u.summer_mw)}</td> : null}
      <td title={UNIT_STATUS_LABEL[u.status]}>{u.status}</td>
      <td className="num r">{u.operating_year ?? "—"}</td>
      {wide ? <td className="num r">{p.age_years ?? "—"}</td> : null}
      <td>
        {u.turbine_model ? (
          <span>
            {u.turbine_oem ? `${u.turbine_oem} ` : ""}
            {u.turbine_model}{" "}
            <Pill tone={u.turbine_model_confidence >= 0.85 ? "ok" : "muted"}>
              {Math.round(u.turbine_model_confidence * 100)} %
            </Pill>
          </span>
        ) : (
          <span style={{ color: "var(--muted-2)" }}>unknown</span>
        )}
      </td>
      <td>{label(FRAME_LABEL, u.frame_class)}</td>
      <td className="num r">
        {p.capacity_factor_latest != null ? fmtPct(p.capacity_factor_latest * 100, 0) : "—"}
      </td>
      <td
        className="num r"
        title={
          p.efficiency_best_source
            ? (EFF_SOURCE[p.efficiency_best_source] ?? p.efficiency_best_source)
            : undefined
        }
      >
        {p.efficiency_best_pct != null ? fmtPct(p.efficiency_best_pct) : "—"}
        {p.efficiency_best_source === "rated_estimate_lhv" ? (
          <span style={{ color: "var(--muted-2)" }}>*</span>
        ) : null}
      </td>
      <td className="num r">{fmtInt(u.summer_run_hours_avg)}</td>
      <td>
        {label(COOLING_TYPE_LABEL, u.inlet_cooling_type)}
        {u.inlet_cooling_vendor ? ` · ${u.inlet_cooling_vendor}` : ""}
        {u.inlet_cooling_year ? ` ${u.inlet_cooling_year}` : ""}
      </td>
      <td className="num r">
        {u.estimate?.mwh_gain_yr != null ? fmtInt(u.estimate.mwh_gain_yr) : "—"}
      </td>
      <td className="num r">
        {u.estimate?.water_m3_yr != null ? fmtInt(u.estimate.water_m3_yr) : "—"}
      </td>
      <td className="num r">{u.score ? u.score.score.toFixed(0) : "—"}</td>
      {wide ? <td>{u.technology ?? "—"}</td> : null}
    </tr>
  );
}
